'use client';

import React, { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  Header,
  HeaderContainer,
  HeaderName,
  HeaderNavigation,
  HeaderMenu,
  HeaderMenuButton,
  HeaderMenuItem,
  HeaderGlobalBar,
  SideNavDivider,
  SideNav,
  SideNavMenu,
  SideNavMenuItem,
  SideNavItems,
  SideNavLink,
  HeaderSideNavItems,
  Button,
  OverflowMenu,
  OverflowMenuItem,
} from '@carbon/react';
import ArrowRight from '@carbon/icons-react/es/ArrowRight.js';
import UserOnline from '@carbon/icons-react/es/UserOnline.js';
import User from '@carbon/icons-react/es/User.js';
import Launch from '@carbon/icons-react/es/Launch.js';
import Search from './search';
import navLinks from './header.json';
import { isAuthenticated, handleSignIn, logout } from '@/utils/helper';
import { appendHostAndBasePath } from '@/utils/urls';
import CommunitySubNav from '@/components/CommunitySubNav/CommunitySubNav';
import './header.scss';
type SubItem = {
  label: string;
  href: string;
  target?: string;
};

type SubSection = {
  title: string;
  titleHref?: string;
  target?: string;
  items: SubItem[];
  viewAll?: SubItem;
  isContentTypes?: boolean;
};

type NavLink = {
  label: string;
  href?: string;
  target?: string;
  subItems?: SubItem[];
  subSections?: SubSection[];
};

type SiteHeaderProps = {
  championsPage?: boolean;
};

const SiteHeader: React.FC<SiteHeaderProps> = ({ championsPage = true }) => {
  const links: NavLink[] = championsPage
    ? navLinks
    : navLinks.filter((link: NavLink) => link.label !== 'Community');
  const pathname = usePathname();

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    setIsLoggedIn(isAuthenticated());
  }, []);

  // Helper function to check if URL is external to developer.ibm.com
  // This function is deterministic and works the same on server and client
  const isExternalUrl = (href: string): boolean => {
    if (!href) return false;

    // If it starts with /, it's internal
    if (href.startsWith('/')) return false;

    try {
      const url = new URL(href);

      // Check if the hostname is not developer.ibm.com
      return !url.hostname.includes('developer.ibm.com');
    } catch {
      // If URL parsing fails, assume it's internal
      return false;
    }
  };

  return (
    <>
      <HeaderContainer
        render={({ isSideNavExpanded, onClickSideNavExpand }) => (
          <Header aria-label="IBM Developer">
            <HeaderMenuButton
              aria-label={isSideNavExpanded ? 'Close menu' : 'Open menu'}
              onClick={onClickSideNavExpand}
              isActive={isSideNavExpanded}
            />

            <a
              href="https://www.ibm.com/"
              title="IBM Logo"
              aria-label="IBM Home"
              className="bx--header__ibm bx--header_logo"
            >
              <svg
                className="Footer-module--logo--3otJM"
                aria-label="IBM Logo"
                width="58"
                height="30"
                viewBox="0 -10 85 52"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g fillRule="evenodd">
                  <path d="M0 32h15.689v-2.038H0zM0 27.721h15.689v-2.038H0zM4.483 23.442h6.724v-2.037H4.483zM4.483 19.164h6.724v-2.038H4.483zM4.483 14.885h6.724v-2.037H4.483zM4.482 10.606h6.724V8.568H4.482zM0 6.327h15.689V4.29H0zM0 2.049h15.689V.011H0zM17.93 29.963V32h16.504a8.521 8.521 0 0 0 5.54-2.037H17.93zM17.93 25.683v2.038h23.914a8.535 8.535 0 0 0 .85-2.038H17.93zM22.412 23.442h6.724v-2.037h-6.724zM40.124 17.126H22.412v2.038H41.77a8.62 8.62 0 0 0-1.645-2.038M22.413 12.848v2.036h17.786a8.612 8.612 0 0 0 1.644-2.037h-19.43zM42.693 6.327a8.447 8.447 0 0 0-.85-2.037H17.93v2.037h24.763zM39.974 2.049a8.521 8.521 0 0 0-5.54-2.037H17.93v2.037h22.044zM22.412 10.606h6.724V8.568h-6.724zM35.453 10.606h7.29a8.603 8.603 0 0 0 .248-2.038h-7.538v2.038zM35.453 21.405v2.037h7.538c0-.703-.09-1.384-.248-2.037h-7.29zM57.457 0H44.825v2.038h13.34zM44.826 32h11.21v-2.038h-11.21zM44.826 27.72h11.21v-2.038h-11.21zM49.309 23.439h6.727v-2.038h-6.727zM49.309 19.159h6.727v-2.038h-6.727zM69.488 32h11.21v-2.038h-11.21zM69.488 27.72h11.21v-2.038h-11.21zM69.488 23.439h6.726v-2.038h-6.726zM69.487 19.159h6.726v-2.038h-6.726zM69.488 14.879h6.726V12.84H63.606l-.707 2.038h5.903l.686-1.94zM61.916 12.84H49.31v2.039h6.726v-1.94l.686 1.94h5.903zM76.213 8.56H65.091l-.707 2.038h11.83zM68.06 0l-.706 2.038h13.344V0zM62.757 32l.72-2.038h-1.432zM61.254 27.72h3.015l.72-2.038h-4.455zM59.743 23.44h6.037l.72-2.039h-7.476zM58.232 19.159h9.06l.719-2.038h-10.5zM49.309 10.598h11.83l-.707-2.038H49.309zM65.868 6.318h14.83V4.28H66.576zM58.947 4.28H44.826v2.038h14.828z"></path>
                </g>
              </svg>
            </a>
            <HeaderName
              className="bx--header__ibmdeveloper"
              href={appendHostAndBasePath('/')}
              aria-label="IBM Developer Home"
              prefix=""
            >
              IBM <strong> Developer</strong>
            </HeaderName>

            <HeaderNavigation aria-label="Main navigation for IBM Developer">
              {links.map(link => {
                if (link.subSections) {
                  return (
                    <HeaderMenu
                      aria-label={`${link.label} menu`}
                      key={link.label}
                      menuLinkName={link.label}
                      className="custom-header-menu"
                    >
                      <div
                        className="submenu-columns-wrapper"
                        style={{
                          display: 'flex',
                          gap: '2rem',
                          backgroundColor: '#ffffff',
                        }}
                      >
                        {link.subSections.map((section, columnIndex) => (
                          <ul
                            key={`menu-column-${link.label}-${columnIndex}`}
                            className={
                              section.isContentTypes
                                ? 'gchColumns content-types-column'
                                : 'gchColumns'
                            }
                            style={{
                              listStyle: 'none',
                              padding: 0,
                              margin: 0,
                              minWidth: section.isContentTypes ? '180px' : '200px',
                            }}
                          >
                            {!section.isContentTypes && (
                              <li>
                                {section.titleHref ? (
                                  <a
                                    href={appendHostAndBasePath(section.titleHref)}
                                    className="headerMenuItemRowHeader section-title-link"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      textDecoration: 'none',
                                      color: 'inherit',
                                    }}
                                  >
                                    <h4 style={{ margin: 0 }}>{section.title}</h4>
                                    <ArrowRight
                                      size={19}
                                      style={{ color: '#0f62fe', fill: '#0f62fe' }}
                                    />
                                  </a>
                                ) : (
                                  <h4 className="headerMenuItemRowHeader">{section.title}</h4>
                                )}
                              </li>
                            )}
                            {section.items.map(item => (
                              <HeaderMenuItem
                                key={`${section.title}-${item.href}`}
                                href={appendHostAndBasePath(item.href)}
                                target={item.target}
                              >
                                <span
                                  style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                                >
                                  {item.label}
                                  {isExternalUrl(item.href) && (
                                    <Launch size={16} aria-label="External link" />
                                  )}
                                </span>
                              </HeaderMenuItem>
                            ))}
                          </ul>
                        ))}
                      </div>
                    </HeaderMenu>
                  );
                } else if (link.subItems) {
                  return (
                    <HeaderMenu
                      aria-label={`${link.label} menu`}
                      key={link.label}
                      menuLinkName={link.label}
                    >
                      <ul
                        key={`menu-column-${link.label}`}
                        className="gchColumns"
                        style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                          minWidth: '275px',
                        }}
                      >
                        {link.subItems.map((item, columnIndex) => (
                          <HeaderMenuItem
                            key={item.href}
                            href={appendHostAndBasePath(item.href)}
                            target={item.target}
                            rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              {item.label}
                              {isExternalUrl(item.href) && (
                                <Launch size={16} aria-label="External link" />
                              )}
                            </span>
                          </HeaderMenuItem>
                        ))}
                      </ul>
                    </HeaderMenu>
                  );
                } else if (link.href) {
                  return (
                    <HeaderMenuItem key={link.href} href={appendHostAndBasePath(link.href)}>
                      {link.label}
                    </HeaderMenuItem>
                  );
                } else {
                  return null;
                }
              })}
            </HeaderNavigation>

            <SideNav
              aria-label="Side navigation for IBM Developer"
              expanded={isSideNavExpanded}
              isPersistent={false}
            >
              <SideNavItems>
                <SideNavMenuItem href={appendHostAndBasePath('/')}>Home</SideNavMenuItem>
                <SideNavDivider />
                <HeaderSideNavItems>
                  {links.map(link => {
                    if (link.subSections) {
                      // Render grouped columns as a flat section in SideNav
                      return (
                        <React.Fragment key={link.label}>
                          <div className="side-menu-left">
                            <h4 className="headerMenuItemRowHeader">{link.label}</h4>
                            {link.subSections.map(section => (
                              <React.Fragment key={section.title}>
                                {section.isContentTypes ? (
                                  // Render content types as direct links without submenu
                                  section.items.map(item => (
                                    <SideNavMenuItem
                                      aria-label={`${link.label} menu`}
                                      key={item.href}
                                      href={appendHostAndBasePath(item.href)}
                                    >
                                      <span
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '0.25rem',
                                        }}
                                      >
                                        {item.label}
                                        {isExternalUrl(item.href) && (
                                          <Launch size={16} aria-label="External link" />
                                        )}
                                      </span>
                                    </SideNavMenuItem>
                                  ))
                                ) : (
                                  <SideNavMenu title={section.title} key={section.title}>
                                    {section.items.map(item => (
                                      <SideNavMenuItem
                                        aria-label={`${link.label} menu`}
                                        key={item.href}
                                        href={appendHostAndBasePath(item.href)}
                                      >
                                        <span
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.25rem',
                                          }}
                                        >
                                          {item.label}
                                          {isExternalUrl(item.href) && (
                                            <Launch size={16} aria-label="External link" />
                                          )}
                                        </span>
                                      </SideNavMenuItem>
                                    ))}
                                    {section.viewAll && (
                                      <SideNavMenuItem
                                        key={section.viewAll.href}
                                        href={appendHostAndBasePath(section.viewAll.href)}
                                      >
                                        {section.viewAll.label}
                                      </SideNavMenuItem>
                                    )}
                                  </SideNavMenu>
                                )}
                              </React.Fragment>
                            ))}
                            <SideNavDivider />
                          </div>
                        </React.Fragment>
                      );
                    } else if (link.subItems) {
                      return (
                        <React.Fragment key={link.label}>
                          <div className="side-menu-left">
                            <h4 className="headerMenuItemRowHeader">{link.label}</h4>

                            {link.subItems.map(item => (
                              <SideNavMenuItem
                                aria-label={`${link.label} menu`}
                                key={item.href}
                                href={appendHostAndBasePath(item.href)}
                                target={item.target}
                              >
                                <span
                                  style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                                >
                                  {item.label}
                                  {isExternalUrl(item.href) && (
                                    <Launch size={16} aria-label="External link" />
                                  )}
                                </span>
                              </SideNavMenuItem>
                            ))}

                            <SideNavDivider />
                          </div>
                        </React.Fragment>
                      );
                    } else if (link.href) {
                      return (
                        <React.Fragment key={link.href}>
                          <SideNavLink
                            aria-label={`${link.label} sub-menu`}
                            key={link.href}
                            href={appendHostAndBasePath(link.href)}
                            target={link.target}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              {link.label}
                              {isExternalUrl(link.href) && (
                                <Launch size={16} aria-label="External link" />
                              )}
                            </span>
                          </SideNavLink>
                          <SideNavDivider />
                        </React.Fragment>
                      );
                    } else {
                      return null;
                    }
                  })}
                </HeaderSideNavItems>
              </SideNavItems>
            </SideNav>

            <HeaderGlobalBar className="global-bar">
              <Search searchPlaceholder="Search all IBM Developer content" />
              <Button
                className="subscribe-button"
                onClick={() =>
                  window.open(
                    'https://www.ibm.com/account/reg/us-en/signup?formid=news-urx-53120',
                    '_blank',
                  )
                }
              >
                Subscribe
              </Button>
              <div data-testid="sign-in-wrapper">
                <OverflowMenu
                  className={`logic-plug-login-state ${isLoggedIn ? 'loggedIn' : ''}`}
                  renderIcon={isLoggedIn ? UserOnline : User}
                  flipped={true}
                  data-testid="user-overflow-menu"
                >
                  <OverflowMenuItem
                    data-testid="dashboard"
                    itemText="My Dashboard"
                    onClick={e => {
                      e.preventDefault();
                      if (isLoggedIn) {
                        window.location.href = '/profile/dashboard';
                      } else {
                        window.location.href = '/profile/auth';
                      }
                    }}
                  />
                  <OverflowMenuItem
                    data-testid="my-ibm-item"
                    itemText="My IBM"
                    href="https://myibm.ibm.com/dashboard/?lnk=mmi"
                  />
                  {isLoggedIn ? (
                    <OverflowMenuItem
                      data-testid="sign-out-menu-item"
                      itemText="Sign Out"
                      href={appendHostAndBasePath('/#$logout=true')}
                      id="profile_sign_out"
                      onClick={e => {
                        e.preventDefault();
                        logout();
                        setIsLoggedIn(false);
                      }}
                    />
                  ) : (
                    <OverflowMenuItem
                      data-testid="sign-in-menu-item"
                      itemText="Sign In"
                      onClick={e => {
                        e.preventDefault();
                        handleSignIn();
                        if (isLoggedIn) {
                          const destination = window.localStorage.getItem('destination');

                          if (destination) {
                            window.localStorage.removeItem('destination');
                            try {
                              const parsed = new URL(destination);
                              window.location.href = parsed.href;
                            } catch (e) {
                              window.location.href = destination;
                            }
                          } else window.location.href = '/profile/dashboard';
                        } else window.location.href = '/profile/auth';
                      }}
                    />
                  )}
                </OverflowMenu>
              </div>
            </HeaderGlobalBar>
          </Header>
        )}
      />

      {/* Community Navigation Bar - Outside Header */}
      {championsPage &&
        links.find(link => link.label === 'Community' && link.subSections) &&
        (pathname.startsWith('/community/') || pathname === '/community') && (
          <CommunitySubNav
            items={links.find(link => link.label === 'Community')?.subSections?.[0]?.items || []}
            title={
              links.find(link => link.label === 'Community')?.subSections?.[0]?.title || 'Champions'
            }
          />
        )}
    </>
  );
};

export default SiteHeader;
